import type { SyncAccountResult } from '../../types';

/**
 * Pre-flight check for "Open DICE Drop".
 *
 * Opening the drop tool only mints a login link — nothing refreshes the DICE
 * tokens on the way. Background account sync is off, so a token is only as
 * fresh as the last manual Sync in Connections, and DICE access tokens live
 * about an hour. Sellers who go straight from this page to the drop tool
 * therefore see "Token Expired" rows and read it as a bug. This module decides,
 * from the Flash Buy rows the page already has, whether to warn first.
 */

/** A Flash Buy row — the subset of the drop-connections payload we need. */
export interface PreflightRow {
  id: number;
  email: string;
  phone: string;
  is_active: boolean;
  is_banned: boolean;
  failed_refresh: boolean;
  dice_drop_enabled: boolean;
  last_synced_at: string | null;
}

/**
 * How old a sync may be before we warn. DICE access tokens expire after about
 * an hour; 45 minutes leaves room for a drop that starts a few minutes after
 * the check.
 */
export const STALE_AFTER_MS = 45 * 60 * 1000;

export interface PreflightReport {
  /** Enabled rows the drop tool will actually receive (active, not banned,
   *  not flagged for reconnect). */
  eligible: PreflightRow[];
  /** Eligible rows synced more than STALE_AFTER_MS ago, or never. */
  stale: PreflightRow[];
  /** Enabled rows core will NOT hand to the drop tool because they need
   *  reconnecting. They silently vanish there, so we name them here. */
  needsReconnect: PreflightRow[];
}

export const assessPreflight = (rows: PreflightRow[], now: number = Date.now()): PreflightReport => {
  const enabled = rows.filter((r) => r.dice_drop_enabled && r.is_active && !r.is_banned);
  const needsReconnect = enabled.filter((r) => r.failed_refresh);
  const eligible = enabled.filter((r) => !r.failed_refresh);
  const stale = eligible.filter((r) => {
    if (!r.last_synced_at) return true;
    const t = new Date(r.last_synced_at).getTime();
    return Number.isNaN(t) || now - t > STALE_AFTER_MS;
  });
  return { eligible, stale, needsReconnect };
};

/** True when there is nothing worth interrupting the seller for. */
export const preflightIsClean = (report: PreflightReport): boolean =>
  report.stale.length === 0 && report.needsReconnect.length === 0;

/** "+1 555 … 0012" style label the rest of the app uses for an account. */
export const rowLabel = (r: Pick<PreflightRow, 'email' | 'phone'>): string => r.email || r.phone || 'account';

export interface PreflightSyncOutcome {
  succeeded: PreflightRow[];
  failed: Array<{ row: PreflightRow; error: string | null }>;
}

/** Join sync results back onto the rows that were synced. */
export const summariseSync = (rows: PreflightRow[], results: SyncAccountResult[]): PreflightSyncOutcome => {
  const byId = new Map(rows.map((r) => [r.id, r]));
  const succeeded: PreflightRow[] = [];
  const failed: PreflightSyncOutcome['failed'] = [];
  for (const res of results) {
    const row = byId.get(res.connection_id);
    if (!row) continue;
    if (res.success) succeeded.push(row);
    else failed.push({ row, error: res.error });
  }
  return { succeeded, failed };
};
